import { useLoader } from "@react-three/fiber";
import { TextureLoader } from "three";
import { IComponentUnion } from "@r3f-component-data-structure";
import { preloadFont } from 'troika-three-text/src';
import { preloadGltf } from "@r3f/components/Model3d/hooks";
import { preInitialiseWorkerWithArrayBuffers } from "@r3f/staticGeometryWorkerController/worker-utils";
import { Jute } from "..";
import { collectAssetUrls } from "../asset-urls";
import { debugLog } from "../debug-log";

interface IPreloadInput {
	componentsById: { [id: string]: IComponentUnion };
	rootId: string;
}

export const useAssetPreloader = ({
	componentsById,
	rootId,
}: IPreloadInput): Promise<any> => {
    // The walk itself lives in asset-urls.ts so live-update cache eviction can ask the same question and
    // get the same answer — see the note there.
    const { imgUrls, model3dUrls, fontPreloads } = collectAssetUrls({ componentsById, rootId });
    debugLog('preload: starting', { imgUrls, model3dUrls, fonts: fontPreloads.length });

    /**
     * Never let one asset hold the boot open.
     *
     * `Jute.loaded()` fires from this promise's `finally`, and `loaded()` is half of what takes the
     * launch spinner down — so a preload that neither resolves nor rejects is an experience that never
     * starts, with nothing on screen or in the console to say why. `Promise.allSettled` upstream guards
     * against REJECTION; it cannot help with a promise that simply never settles.
     */
    const settle = (label: string, p: Promise<unknown>): Promise<void> =>
        p.then(
            () => { debugLog(`preload: ${label} ok`); },
            (err) => { console.warn(`jute preloader: ${label} failed — continuing without it`, err); }
        );
    // let hlsData: IHlsPreloadData[] = [];
    let promises: Promise<any>[] = fontPreloads.map(
        ({ url, characters, sdfGlyphSize }) =>
            new Promise(res => {
                preloadFont({ font: url, characters, sdfGlyphSize }, () => {
                    res(true)
                });
            })
    );

    // TODO: fix video preload as id always undefined (see collectAssetUrls for where it would slot in)

    let imgTexturePromise = new Promise(res => {
        useLoader.preload(TextureLoader, imgUrls as any, (loader) => {
            loader.manager.onLoad = () => {
                res(true)
            }
            // A failing texture is otherwise silent: the manager still drains, so `onLoad` fires and the
            // missing image only shows up as a blank mesh with nothing said anywhere.
            loader.manager.onError = (url) => console.warn('jute preloader: texture failed to load', url);
        });
    });

    // `preloadGltf` rejects if ANY model fails, and this used to have no rejection path: `res` was never
    // called, the promise never settled, and the launch spinner span forever. The missing `return` below
    // was the same fault by another route — a null result fell through into the worker call with `null`.
    const model3dPromise = new Promise((res, rej) => {
        preloadGltf(model3dUrls)
            .then((data) => {
                if (data === null) return res(true);
                return preInitialiseWorkerWithArrayBuffers(Jute.staticGeometryWorkerController, data).then(() => res(true));
            })
            .catch(rej);
    })

    // const model3dPromise = preloadGltf(model3dUrls)

    if (!promises.length) promises = [new Promise(res => res(true))]
    if (!imgUrls.length) imgTexturePromise = new Promise(res => res(true));

    // let vidTexturePromise = useHlsPreloader(hlsData);
    // if (!hlsData.length) vidTexturePromise = new Promise(res => res(true));

    // let vidTexturePromise = new Promise(res => res(true));

    // collect and return as one promise
    // return Promise.all([...promises, imgTexturePromise, vidTexturePromise, model3dPromise])
    //     .then(() => console.log('useAssetPreloader: asset preloading complete'));

    return Promise.all([
        settle('fonts', Promise.all(promises)),
        settle('textures', imgTexturePromise),
        settle('models', model3dPromise)
    ]).then(() => console.log('useAssetPreloader: asset preloading complete'));
};
